import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import joinClasses from 'webapp/helpers/join-classes';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import BoardManualSyncButtonComponent from 'webapp/components/board/manual-sync-button';
import type { Action as ActionBase } from 'webapp/components/boards/index/item-base';
import BoardsIndexItemBaseComponent from 'webapp/components/boards/index/item-base';
import CommonGenieButtonComponent from 'webapp/components/common/genie/button';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataPerformanceSummaryButtonComponent from 'webapp/components/data-performance/summary-button';
import DataStatusTextComponent from 'webapp/components/data-status-text';
import DummyCellComponent from 'webapp/components/dummy-cell';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import FlexibleToggleCellComponent from 'webapp/components/flexible-toggle-cell';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import OptionsButtonComponent from 'webapp/components/options-button';
import TagContainerComponent from 'webapp/components/tag-container';
import ToolTipComponent from 'webapp/components/tool-tip';
import UsedSourcesListComponent from 'webapp/components/used-sources-list';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

type Action = ActionBase | 'openPreview' | 'editBoard' | 'toggleFavorite' | 'openWallShareSettings';
export type Option = PopupListItem<Action>;

export default class BoardsIndexTableRowComponent extends BoardsIndexItemBaseComponent {
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;

	@tracked showAll = false;

	get showPerformanceSummary() {
		return this.mmDataPerformanceSummariesService.showInUI;
	}

	get options(): Option[] {
		const options: Option[] = [];

		if (this.isDeleted) {
			if (this.args.model.canCurrentUserEditWithoutDeleted) {
				options.push(
					{
						icon: 'Restore',
						text: 'Restore',
						action: 'restore',
					},
					{
						class: 'delete-permanently',
						text: 'Delete permanently',
						action: 'deletePermanently',
						icon: 'Trash',
					},
				);
			}
			return options;
		}

		options.push({
			icon: 'Databoards',
			text: 'View',
			action: 'openPreview',
		});

		if (this.args.model.canCurrentUserEdit) {
			options.push({
				icon: 'Pencil',
				text: 'Edit',
				action: 'editBoard',
			});
		}

		options.push(
			...(this.args.model.getAdditionalOptions(
				!this.isTeamSectionItem,
				false,
				this.isTeamSectionItem,
			) as Option[]),
		);

		return options;
	}

	get databoardRenamedTrackingObj() {
		return {
			event: TrackingEvent.DATABOARD_RENAME,
			data: { databoard_id: this.args.model.id, origin: this.trackOrigin },
		};
	}

	get showStatusText() {
		return this.isActiveUserViewer || this.isDemoAccount;
	}

	get imageClickHandler() {
		if (this.isDeleted) {
			return;
		}
		return this.openPreview;
	}

	@action
	toggleShowAll() {
		this.showAll = !this.showAll;
	}

	@action
	openEnableCustomDataUpdatePopup() {
		this.mmBoardsService.openEnableCustomDataUpdatePopup(this.args.model, this.trackOrigin);
	}
	<template>
		{{#if this.isVisibleOnScreen}}

			<tr
				data-id={{@model.id}}
				class={{joinClasses
					(array
						"table-row"
						"board"
						(if this.showAll "all-shown")
						(if @hideSort "hideSort")
						(if this.isSelected "selected")
					)
				}}
			>
				<td class="cc no-grow first">
					{{#if this.showDragHandle}}
						<div class="drag" data-id={{@model.id}}>
							<CommonUiIconComponent @name="ArrowsMove" @size="md" />
						</div>
					{{/if}}

					<FormCheckboxComponent
						@name="boards-table-row"
						@selected={{this.isSelected}}
						@theme="blue"
						@onChange={{this.selectItem}}
					/>
				</td>

				<td class="c0 board no-grow">

					<BoardImagePreviewComponent
						@image={{@model.image}}
						@color={{@model.color}}
						@theme={{@model.bgTheme}}
						@isLazyLoaded={{true}}
						@emblemTypeName={{this.emblemTypeName}}
						@onImageClick={{this.imageClickHandler}}
					/>

				</td>
				<td class="c1">

					<div class="flex">
						<EditableModelPropertyComponent
							@model={{@model}}
							@canEdit={{this.canEditBoardName}}
							@editableProperty="name"
							@trackingObj={{this.databoardRenamedTrackingObj}}
						/>

						<VerificationBadgeComponent
							@verification={{@model.verificationInfo}}
							@entityLabel="databoard"
							@size="sm"
						/>

						<div class="labelicons">
							{{#if @model.favorite}}
								<span class="labelicon">
									<CommonUiIconComponent @name="StarFill" @size="sm" />
								</span>
							{{/if}}
						</div>
					</div>

					<small>by {{@model.createdBy.name}} on {{@model.createdText}}</small>

				</td>

				{{#if this.showPerformanceSummary}}
					<td class="data-performance-summary-cell prio-low">
						<div class="center">
							<DataPerformanceSummaryButtonComponent
								@object={{this.dataPerformanceSummaryObject}}
								@trackingOrigin="databoards"
							/>
						</div>
					</td>
				{{/if}}

				<td class="c2 prio-medium" data-label="Data sources: ">
					<UsedSourcesListComponent
						@sources={{@model.sourcesList}}
						@maxShownSourcesCount={{3}}
					/>
				</td>

				<td class="perm prio-low" data-label="Shared with: ">
					<div class={{@model.permissionColor}}>
						{{#if @model.permissionIcon}}
							<CommonUiIconComponent @name={{@model.permissionIcon}} @size="sm" />
						{{/if}}
						<span>{{@model.permissionText}}</span>
						{{#if @model.users}}
							<ToolTipComponent
								@text={{@model.permissionTooltip}}
								@position="bottom"
								@delay={{200}}
								@maxWidth={{180}}
							/>
						{{/if}}
					</div>
				</td>

				<td class="c3 prio-high status" data-label="Last update: ">
					<div class="center">
						{{#if @model.isAnythingToSolve}}
							{{#if this.showStatusText}}
								<span class="{{@model.statusClass}} no-pointer">
									{{@model.statusText}}
								</span>
							{{else}}
								<a
									role="button"
									class="button theme-green"
									{{on "click" this.resolveSources}}
								>
									{{this.greenbuttonText}}
								</a>
							{{/if}}
						{{else}}
							{{#if @model.isRandom}}
								<span class="time">
									<CommonUiIconComponent @name="Refresh" @size="xs" />
									sample data
								</span>
							{{else if this.isDeleted}}
								<span>{{@model.deletedText}}</span>
							{{else if this.isError}}
								{{#if this.showStatusText}}
									<span class="{{@model.statusClass}} no-pointer">
										<CommonUiIconComponent @name="AlertFill" @size="xs" />
										{{@model.statusText}}
									</span>
								{{else}}
									<span
										role="button"
										class={{@model.statusClass}}
										{{on "click" this.resolveBoardConnections}}
									>
										<CommonUiIconComponent @name="AlertFill" @size="xs" />
										{{@model.statusText}}
									</span>
									<ToolTipComponent @text="Resolve" />
								{{/if}}
							{{else if @model.lastSyncText}}
								<div class="status__wrapper">
									<span class={{@model.statusClass}}>
										<DataStatusTextComponent
											@text={{@model.lastSyncText}}
											@context="board"
											@lastSync={{@model.lastSync}}
											@openEnableCustomDataUpdatePopup={{this.openEnableCustomDataUpdatePopup}}
										/>
									</span>
									<BoardManualSyncButtonComponent
										@model={{@model}}
										@trackingOrigin={{this.trackOrigin}}
									/>
								</div>

							{{/if}}
						{{/if}}
					</div>
				</td>

				<td class="c4 prio-low" data-label="Tags: ">
					<div>
						<TagContainerComponent
							@model={{@model}}
							@canEdit={{this.canCurrentUserEdit}}
						/>
					</div>
				</td>

				<td class="c6 button-ct last">
					<div class="center">
						{{#if this.showAiChatButton}}
							<CommonGenieButtonComponent
								@text="Analyze"
								@openChat={{this.openGenie}}
							/>
						{{/if}}
						<OptionsButtonComponent
							@options={{this.options}}
							@onAction={{this.doOptionsAction}}
							@hideDropdownSearch={{true}}
						/>
					</div>
				</td>

				<FlexibleToggleCellComponent
					@showAll={{this.showAll}}
					@toggleShowAll={{this.toggleShowAll}}
				/>

			</tr>

		{{else}}

			<tr
				data-id={{@model.id}}
				class="table-row board"
				{{visibleCheckItem this.visibleChanged false}}
			>
				<DummyCellComponent />
			</tr>

		{{/if}}
	</template>
}
